import { FlexDirections, GreyFlex } from '@ydistri/ds';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../../../store';
import { Steps } from 'antd';
import EntityListStepImport from './EntityListStepImport';
import { StepProps } from 'antd/es/steps';
import {
  EntityListImportStep,
  EntityListImportStepRunning,
  getNumericStep,
  resetEntityListItems,
  setActiveScreenType,
  setProductItemsContent,
} from '../../entityListAdministrationSlice';
import EntityListStepValidation from './EntityListStepValidation';
import { EntityListTypeSupported, IMPORT_STEPS } from '../../entityListsLib';
import { styled } from 'styled-components';
import ExitEditModeButton from '../../../../../components/buttons/ExitEditModeButton';
import { ActiveScreenType } from '../../../common/administrationItemsLib';
import { LoadingOutlined } from '@ant-design/icons';
import { useCurrentEntityListState } from '../../hooks/useCurrentEntityListState';
import useDeleteEntityListImport from '../../hooks/useDeleteEntityListImport';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { computeRemSize } from '@ydistri/theme';
import ImportWaitingHint from '../../components/ImportWaitingHint';

export type YStepProps = { stepId: EntityListImportStep; content: React.ReactNode } & StepProps;

export const getSteps = (
  entityListType: EntityListTypeSupported,
  currentStepId: EntityListImportStep,
  runningData?: EntityListImportStepRunning,
): YStepProps[] => {
  const getStepStatus = (stepId: EntityListImportStep): StepProps['status'] => {
    const currentStep = getNumericStep(currentStepId);
    const step = getNumericStep(stepId);

    if (runningData?.[stepId]) return 'process';
    if (currentStep > step) return 'finish';
    return undefined;
  };

  const statuses: Record<EntityListImportStep, StepProps['status']> = {
    [IMPORT_STEPS.ITEM_IMPORT]: getStepStatus(IMPORT_STEPS.ITEM_IMPORT),
    [IMPORT_STEPS.ITEM_VALIDATION]: getStepStatus(IMPORT_STEPS.ITEM_VALIDATION),
  };

  return [
    {
      stepId: IMPORT_STEPS.ITEM_IMPORT,
      title: 'Import items',
      icon: statuses[IMPORT_STEPS.ITEM_IMPORT] === 'process' ? <LoadingOutlined /> : undefined,
      content: <EntityListStepImport entityListType={entityListType} />,
      status: statuses[IMPORT_STEPS.ITEM_IMPORT],
    },
    {
      stepId: IMPORT_STEPS.ITEM_VALIDATION,
      title: 'Validate items',
      icon: statuses[IMPORT_STEPS.ITEM_VALIDATION] === 'process' ? <LoadingOutlined /> : undefined,
      content: <EntityListStepValidation entityListType={entityListType} />,
      status: statuses[IMPORT_STEPS.ITEM_VALIDATION],
    },
  ];
};

const StepsRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ButtonWrapper = styled.div`
  width: ${computeRemSize(50)};
`;

const StepsWrapper = styled.div`
  display: flex;
  min-width: ${computeRemSize(600)};
  max-width: ${computeRemSize(800)};
  align-items: center;
  align-self: center;
  gap: 4rem;
`;

interface EntityListStepsProps {
  entityListType: EntityListTypeSupported;
}

const EntityListSteps: React.FC<EntityListStepsProps> = ({ entityListType }) => {
  const entityListState = useCurrentEntityListState(entityListType);
  const dispatch = useAppDispatch();

  // Addressed to the list the user left, which is no longer the selected one when another list was opened meanwhile.
  const exitEditMode = useCallback(
    (entityListId: number) => {
      dispatch(
        setActiveScreenType({
          entityListType,
          entityListId,
          data: ActiveScreenType.DETAIL,
          forced: true,
        }),
      );
      dispatch(setProductItemsContent({ entityListType, entityListId, data: '' }));
      dispatch(resetEntityListItems({ entityListType, entityListId }));
    },
    [dispatch, entityListType],
  );

  const [deleteEntityListImport, deleteStatus] = useDeleteEntityListImport(
    entityListType,
    exitEditMode,
  );

  const importStep = entityListState?.importStep ?? IMPORT_STEPS.ITEM_IMPORT;
  const importStepRunning = entityListState?.importStepRunning;

  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );

  // The steps stay until the cancellation settles, so a cancellation that waits can say why.
  const onExitEditMode = useCallback(() => {
    if (entityListState?.verificationTimeStamp) {
      deleteEntityListImport();
    } else if (selectedEntityList) {
      exitEditMode(selectedEntityList.entityListId);
    }
  }, [
    deleteEntityListImport,
    entityListState?.verificationTimeStamp,
    exitEditMode,
    selectedEntityList,
  ]);

  const steps = useMemo(
    () => getSteps(entityListType, importStep, importStepRunning),
    [entityListType, importStep, importStepRunning],
  );

  if (!selectedEntityList) {
    return 'No list selected';
  } else {
    return (
      <>
        <StepsRow>
          <StepsWrapper>
            <Steps current={getNumericStep(importStep) - 1} items={steps} />
          </StepsWrapper>
          <ButtonWrapper>
            <ExitEditModeButton
              onClick={onExitEditMode}
              disabled={deleteStatus === QueryStatus.pending}
            />
          </ButtonWrapper>
        </StepsRow>
        <ImportWaitingHint pending={deleteStatus === QueryStatus.pending} />
        <GreyFlex $flexDirection={FlexDirections.COLUMN}>
          {steps.find(s => s.stepId === importStep)?.content}
        </GreyFlex>
      </>
    );
  }
};

export default EntityListSteps;
